import { useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ACHIEVEMENT_TOAST_STAGGER_DELAY, TOAST_AUTO_CLOSE_DELAY } from '../constants/timing';
import { logError } from '../utils/logger';
import achievementService from '../services/achievementService';

const useAchievements = () => {
  const { showAchievementToast, addToast } = useToast();
  const { user, isAuthenticated } = useAuth();

  const handleNewAchievements = useCallback(async (newAchievements) => {
    if (!newAchievements || !Array.isArray(newAchievements) || newAchievements.length === 0) {
      return;
    }

    // Show toast notifications for each new achievement
    newAchievements.forEach((achievement, index) => {
      if (!achievement) return; // Guard against null/undefined achievements
      
      // Delay each toast slightly to create a nice staggered effect
      setTimeout(() => {
        showAchievementToast(achievement);
      }, index * ACHIEVEMENT_TOAST_STAGGER_DELAY);
    });

    // --- FIX APPLIED HERE ---
    // Create a new array containing only valid, non-null/undefined IDs.
    const validAchievementIds = newAchievements
      .map(ach => ach?.id) // Safely access the id property
      .filter(id => id != null); // Filters out both null and undefined values

    // Mark achievements as notified only if there are valid IDs to send.
    if (validAchievementIds.length > 0 && isAuthenticated) {
      try {
        // Use the service layer instead of a direct API call
        await achievementService.markAchievementsAsNotified(validAchievementIds);
      } catch (error) {
        logError('Error marking achievements as notified', error);
        addToast({
          type: 'error',
          message: 'Failed to update achievement notifications. Please try again.',
          autoClose: true,
          autoCloseDelay: TOAST_AUTO_CLOSE_DELAY
        });
      }
    }
  }, [showAchievementToast, isAuthenticated, addToast]);

  const processGameCompletionResponse = useCallback(async (response) => {
    if (!response) return response;
    
    // Check if response has newAchievements with proper validation
    if (response.newAchievements && Array.isArray(response.newAchievements) && response.newAchievements.length > 0) {
      await handleNewAchievements(response.newAchievements);
    }
    
    return response;
  }, [handleNewAchievements]);

  return {
    handleNewAchievements,
    processGameCompletionResponse
  };
};

export default useAchievements;